import axios, { AxiosError } from "axios";
import { auth } from "@/config/host.config";
import store from "@/store/index";
import router from "@/router";
import { getPayload } from "@/services/accessDecoder";
import { AuthAPI } from "./user-authorized-module";
const axiosApiInstance = axios.create();

export function ACCESS_REQUEST(): string {
  const storeAccess = store.getters.getAccess;
  const sessionAccess = sessionStorage.getItem("access");
  const access = storeAccess ? storeAccess : sessionAccess ? sessionAccess : "";
  return access;
}
export function REFRESH_REQUEST(): { refreshtoken: string } {
  return { refreshtoken: store.getters.getRefreshToken };
}

// let delay = false;
let refreshTokenPromise: Promise<any> | null = null;

//send refresh yoken & get new access token
// export async function refreshAccessToken(): Promise<
//   { access: string; refreshtoken: string } | undefined
// > {
//   delay = true;
//   const url = auth.refresh;
//   try {
//     const refreshRes = await axios.post(url, REFRESH_REQUEST());
//     if (refreshRes.status === 200) {
//       store.dispatch("setAccessToken", refreshRes.data.access);
//       store.dispatch("setRefreshToken", refreshRes.data.refreshtoken);
//       console.log("setRefreshToken: " + refreshRes.data.refreshtoken);

//       const payload = getPayload(refreshRes.data.access);
//       store.dispatch("setUserRoles", payload.rules);
//       delay = false;
//       return refreshRes.data;
//     }
//   } catch (error) {
//     delay = false;
//     store.dispatch("onLogout");
//     router.push("/login");
//     return undefined;
//   }
// }

// Токен действительно невалиден — сессию уже не спасти.
function isAuthError(error: AxiosError): boolean {
  const status = error?.response?.status;
  return status === 400 || status === 401 || status === 403;
}

// Транзитный сбой: сети нет / таймаут / 5xx — имеет смысл ретраить,
// при этом сессию НЕ рвём.
function isTransientError(error: AxiosError): boolean {
  if (!error) return true;
  if (!error.response) return true; // обрыв сети, таймаут, CORS, connection refused
  const status = error.response.status;
  return status >= 500 || status === 408 || status === 425 || status === 429;
}

const REFRESH_MAX_ATTEMPTS = 3; // первая попытка + 2 ретрая
const REFRESH_RETRY_DELAY_MS = 1000;

export async function refreshAccessToken(): Promise<{
  access: string;
  refreshtoken: string;
}> {
  // Если уже выполняется запрос на обновление, ждём его (single-flight)
  if (refreshTokenPromise) {
    return refreshTokenPromise;
  }

  refreshTokenPromise = (async () => {
    let lastError: unknown = null;

    for (let attempt = 1; attempt <= REFRESH_MAX_ATTEMPTS; attempt++) {
      try {
        const refreshRes = await axios.post(auth.refresh, {
          refreshtoken: REFRESH_REQUEST(),
        });

        if (refreshRes.data && refreshRes.data.access) {
          store.dispatch("setAccessToken", refreshRes.data.access);
          if (refreshRes.data.refreshtoken) {
            store.dispatch("setRefreshToken", refreshRes.data.refreshtoken);
          }
          console.log("setRefreshToken: " + refreshRes.data.refreshtoken);

          // Декод access-токена не должен рвать сессию: токены уже сохранены.
          try {
            const payload = getPayload(refreshRes.data.access);
            if (payload && payload.rules) {
              store.dispatch("setUserRoles", payload.rules);
            }
          } catch (e) {
            console.warn("refreshAccessToken: access token decode failed", e);
          }
          return refreshRes.data;
        }

        lastError = new Error("Refresh response missing access token");
      } catch (error) {
        lastError = error;
        const axiosErr = error as AxiosError;

        // Токен отклонён сервером (невалиден/истёк) — честный логаут.
        if (isAuthError(axiosErr)) {
          store.dispatch("onLogout");
          router.push("/login");
          throw error;
        }

        // Не auth и не транзитная (неожиданный статус) — всё равно не логиним,
        // даём шанс ретраю, но сигнализируем в лог.
        if (!isTransientError(axiosErr)) {
          console.warn(
            "refreshAccessToken: unexpected error, will retry",
            axiosErr
          );
        }
        // Иначе транзитный сбой — просто ретрай на следующей итерации.
      }

      if (attempt < REFRESH_MAX_ATTEMPTS) {
        console.warn(
          `refreshAccessToken: transient failure, retry ${attempt}/${
            REFRESH_MAX_ATTEMPTS - 1
          }`
        );
        await new Promise((r) =>
          setTimeout(r, REFRESH_RETRY_DELAY_MS * attempt)
        );
      }
    }

    // Все попытки пришли с транзитной ошибкой.
    // Сессию НЕ рвём — следующий запрос попробует refresh снова.
    console.warn("refreshAccessToken: retries exhausted", lastError);
    throw lastError;
  })().finally(() => {
    refreshTokenPromise = null;
  });

  return refreshTokenPromise;
}

// Request interceptor for API calls
axiosApiInstance.interceptors.request.use(
  (request) => {
    if (request.headers) {
      request.headers.Authorization = `Bearer ${ACCESS_REQUEST()}`;
    }
    return request;
  },
  (error) => {
    Promise.reject(error);
  }
);

// Response interceptor for API calls
// axiosApiInstance.interceptors.response.use(
//   (response) => {
//     return response;
//   },
//   async function (error) {
//     const originalRequest = error.config;

//     if (error.response.status === 401 && !originalRequest._retry) {
//       if (!delay) {
//         const access_token = await refreshAccessToken();
//         if (access_token) {
//           axios.defaults.headers.common[
//             "Authorization"
//           ] = `Bearer ${access_token.access}`;
//           originalRequest._retry = true;
//           return axiosApiInstance(originalRequest);
//         }
//       } else {
//         setTimeout(() => {
//           axios.defaults.headers.common[
//             "Authorization"
//           ] = `Bearer ${ACCESS_REQUEST()}`;
//           originalRequest._retry = true;
//           return axiosApiInstance(originalRequest);
//         }, 1200);
//       }
//     }

//     return Promise.reject(error);
//   }
// );

axiosApiInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const tokens = await refreshAccessToken();

        // Обновляем заголовок для текущего запроса
        originalRequest.headers.Authorization = `Bearer ${tokens?.access}`;

        // Повторяем оригинальный запрос
        return axiosApiInstance(originalRequest);
      } catch (refreshError) {
        // Если обновление токена не удалось, прерываем цепочку
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosApiInstance;
